import { Fragment } from "react";
import { Link } from "react-router-dom";

import { CaretRightIcon } from "@/modules/common/assets/icons";
import { cn } from "@/libs/cn";

export type PageBreadcrumbItem = {
  label: string;
  /** Sem `to` = segmento atual (estilo #101A25) */
  to?: string;
};

type PageBreadcrumbProps = {
  items: PageBreadcrumbItem[];
  className?: string;
};

/**
 * Componente de breadcrumb para navegação hierárquica de páginas.
 *
 * Props:
 * - `items` (PageBreadcrumbItem[]): lista ordenada dos segmentos do breadcrumb.
 *    - `label` (string): texto exibido no segmento.
 *    - `to` (string, opcional): rota do segmento.
 *      - Quando informado, o segmento é renderizado como link.
 *      - Quando omitido, o segmento é tratado como página atual (texto sem link).
 * - `className` (string, opcional): classes extras para customizar margem/spacing/estilo externo.
 *
 * Exemplo:
 * items={[
 *   { label: "Unidades", to: "/units" },
 *   { label: "Nome da unidade", to: "/consumer-units/123/details" },
 *   { label: "Editar UC" },
 * ]}
 */
const PageBreadcrumb = ({ items, className }: PageBreadcrumbProps) => {
  return (
    <nav aria-label="Breadcrumb" className={cn("flex flex-wrap items-center gap-1.5 text-sm", className)}>
      {items.map((item, index) => (
        <Fragment key={`breadcrumb-item-${index}`}>
          {index > 0 ? <CaretRightIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden /> : null}
          {item.to ? (
            <Link to={item.to} className="font-normal text-gray-500 transition-colors hover:text-gray-600">
              {item.label}
            </Link>
          ) : (
            <span className="font-normal text-[#101A25]">{item.label}</span>
          )}
        </Fragment>
      ))}
    </nav>
  );
};

export default PageBreadcrumb;
